'use client';

import { Box, IconButton, Popover, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import SimpleIconsDiscord from '../icons/SimpleIconsDiscord';
import SimpleIconsWechat from '../icons/SimpleIconsWechat';
import WeChatQRCodes from './WeChatQRCodes';

interface FloatingCommunityLinksProps {
  publicQrSrc: string;
  groupQrSrc: string;
}

export default function FloatingCommunityLinks({ publicQrSrc, groupQrSrc }: FloatingCommunityLinksProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isCommunityVisible, setIsCommunityVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const wechatButtonRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);

  // 平台颜色
  const platforms = {
    discord: {
      color: '#5865F2',
      hoverColor: '#4752c4',
      url: 'https://discord.gg/frrmUpUeSb',
      icon: 'discord',
    },
    wechat: {
      color: '#07C160',
      hoverColor: '#06a954',
      url: '#',
      icon: 'wechat',
    },
  };

  // 基础黑色背景
  const darkBgColor = 'rgba(17, 19, 26, 0.9)';

  // 监听社区区域可见性
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => setIsCommunityVisible(entry.isIntersecting)),
      { threshold: 0.1 },
    );

    const target = document.getElementById('community');
    if (target) observer.observe(target);
    observerRef.current = observer;

    return () => observer.disconnect();
  }, []);

  // 处理 Popover 开关
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleWeChatClick = () => {
    if (anchorEl) {
      setAnchorEl(null);
    } else if (wechatButtonRef.current) {
      setAnchorEl(wechatButtonRef.current);
    }
  };

  // 获取按钮展示模式 - 文本或图标
  const showTextLabels = useMediaQuery(theme.breakpoints.up('xl'));

  // 按钮基础样式
  const buttonStyle = {
    position: 'relative',
    height: showTextLabels ? '48px' : '44px',
    width: showTextLabels ? '140px' : '44px',
    minWidth: showTextLabels ? '140px' : 0,
    padding: showTextLabels ? '0 20px' : 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    overflow: 'hidden',
    border: '1px solid',
    bgcolor: darkBgColor,
    backdropFilter: 'blur(8px)',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: 'rgba(255,255,255,0.03)',
      zIndex: 0,
    },
    '@media (hover: hover)': {
      '&:hover': {
        transform: 'scale(1.1)',
        borderColor: 'rgba(255,255,255,0.3)',
      },
      '&:active': {
        transform: 'scale(0.98)',
        transition: 'all 0.1s',
      },
    },
  };

  // 图标样式
  const iconStyle = {
    width: showTextLabels ? 22 : 20,
    height: showTextLabels ? 22 : 20,
    filter: 'brightness(0) invert(1)',
    position: 'relative',
    zIndex: 2,
    transition: 'transform 0.3s ease',
    '@media (hover: hover)': {
      '.MuiBox-root:hover &': {
        transform: 'scale(1.1)',
      },
    },
  };

  // 文字样式
  const textStyle = {
    color: 'white',
    fontWeight: 600,
    display: showTextLabels ? 'inline' : 'none',
    ml: showTextLabels ? 2 : 0,
    fontSize: '0.95rem',
    whiteSpace: 'nowrap',
    position: 'relative',
    zIndex: 2,
    textShadow: '0 1px 3px rgba(0,0,0,0.2)',
  };

  return (
    <div
      className={`fixed z-40 flex flex-col gap-5 transition-all duration-500 ${
        isCommunityVisible ? 'opacity-0 pointer-events-none translate-x-10' : 'opacity-100 translate-x-0'
      } right-4 ${showTextLabels ? 'lg:right-8' : 'bottom-24'} ${showTextLabels ? 'top-1/2 -translate-y-1/2' : ''}`}
    >
      {/* Discord Button */}
      <Box
        component="a"
        href={platforms.discord.url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          ...buttonStyle,
          borderColor: alpha(platforms.discord.color, 0.5),
          boxShadow: `0 10px 25px ${alpha(platforms.discord.color, 0.15)}, 0 0 5px ${alpha(platforms.discord.color, 0.2)} inset`,
          '@media (hover: hover)': {
            '&:hover': {
              boxShadow: `0 15px 30px ${alpha(platforms.discord.color, 0.25)}, 0 0 8px ${alpha(platforms.discord.color, 0.3)} inset`,
              borderColor: alpha(platforms.discord.color, 0.7),
              transform: 'scale(1.1)',
            },
            '&:hover::after': {
              opacity: 0.8,
            },
            '&:hover::before': {
              opacity: 0.7,
            },
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(to right, transparent, ${alpha(platforms.discord.color, 0.7)}, transparent)`,
            opacity: 0.5,
            transition: 'opacity 0.4s ease',
            zIndex: 1,
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            right: '-50%',
            bottom: '-50%',
            background: `radial-gradient(circle, ${alpha(platforms.discord.color, 0.15)} 0%, transparent 70%)`,
            opacity: 0,
            transition: 'opacity 0.5s ease',
            zIndex: 0,
          },
        }}
        aria-label="Join our Discord community"
      >
        <Box sx={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center' }}>
          <Box component={SimpleIconsDiscord} sx={iconStyle} />
          <Box component="span" sx={textStyle}>
            Discord
          </Box>
        </Box>
      </Box>

      {/* WeChat Button */}
      <Box
        component="button"
        ref={wechatButtonRef}
        onClick={handleWeChatClick}
        sx={{
          ...buttonStyle,
          borderColor: alpha(platforms.wechat.color, 0.5),
          boxShadow: `0 10px 25px ${alpha(platforms.wechat.color, 0.15)}, 0 0 5px ${alpha(platforms.wechat.color, 0.2)} inset`,
          '@media (hover: hover)': {
            '&:hover': {
              boxShadow: `0 15px 30px ${alpha(platforms.wechat.color, 0.25)}, 0 0 8px ${alpha(platforms.wechat.color, 0.3)} inset`,
              borderColor: alpha(platforms.wechat.color, 0.7),
              transform: 'scale(1.1)',
            },
            '&:hover::after': {
              opacity: 0.8,
            },
            '&:hover::before': {
              opacity: 0.7,
            },
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(to right, transparent, ${alpha(platforms.wechat.color, 0.7)}, transparent)`,
            opacity: 0.5,
            transition: 'opacity 0.4s ease',
            zIndex: 1,
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            right: '-50%',
            bottom: '-50%',
            background: `radial-gradient(circle, ${alpha(platforms.wechat.color, 0.15)} 0%, transparent 70%)`,
            opacity: 0,
            transition: 'opacity 0.5s ease',
            zIndex: 0,
          },
        }}
        aria-label="Join our WeChat community"
      >
        <Box sx={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center' }}>
          <Box component={SimpleIconsWechat} sx={iconStyle} />
          <Box component="span" sx={textStyle}>
            WeChat
          </Box>
        </Box>
      </Box>

      {/* 微信 QR Code Popover */}
      <Popover
        id="wechat-qr-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        disableRestoreFocus
        anchorOrigin={{
          vertical: 'center',
          horizontal: !isMobile ? 'left' : 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: !isMobile ? 'right' : 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              width: '450px',
              maxWidth: '95vw',
              p: 2,
              pt: 3.5,
              pb: 4,
              borderRadius: '16px',
              bgcolor: darkBgColor,
              backdropFilter: 'blur(15px)',
              boxShadow: `0 25px 50px rgba(0,0,0,0.3), 0 5px 15px ${alpha(platforms.wechat.color, 0.2)}`,
              border: '1px solid',
              borderColor: alpha(platforms.wechat.color, 0.4),
              overflow: 'visible',
              marginLeft: !isMobile ? '-15px' : 0,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '12px',
                left: '12px',
                right: '12px',
                height: '1px',
                background: `linear-gradient(to right, transparent, ${alpha(platforms.wechat.color, 0.5)}, transparent)`,
                opacity: 0.8,
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '12px',
                left: '12px',
                right: '12px',
                height: '1px',
                background: `linear-gradient(to right, transparent, ${alpha(platforms.wechat.color, 0.5)}, transparent)`,
                opacity: 0.8,
              },
            },
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, pointerEvents: 'auto' }}>
          {isMobile && (
            <IconButton
              aria-label="close"
              onClick={handlePopoverClose}
              sx={{
                position: 'absolute',
                right: -8,
                top: -18,
                width: 32,
                height: 32,
                bgcolor: alpha(platforms.wechat.color, 0.15),
                color: 'rgba(255,255,255,0.9)',
                border: '1px solid',
                borderColor: alpha(platforms.wechat.color, 0.4),
                boxShadow: `0 4px 10px rgba(0,0,0,0.25), 0 0 5px ${alpha(platforms.wechat.color, 0.2)}`,
                zIndex: 2,
                transition: 'all 0.2s ease',
                '@media (hover: hover)': {
                  '&:hover': {
                    bgcolor: alpha(platforms.wechat.color, 0.25),
                    color: 'white',
                    transform: 'rotate(90deg)',
                  },
                },
              }}
            >
              <X size={16} />
            </IconButton>
          )}

          <WeChatQRCodes
            containerSx={{
              gap: 1,
            }}
            publicQrSrc={publicQrSrc}
            groupQrSrc={groupQrSrc}
          />
        </Box>
      </Popover>
    </div>
  );
}
